import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { Menu, MenuItem } from '@material-ui/core';
import AgentQAItemsModel from 'models/AgentQAItemsModel';
import { AgentQAModel } from 'models/AgentQAModel';
import { IAgentQAGroupedItem, AgentQAJobStatus } from 'types/agentQA';
import { exportAgentQACSV } from '../utils/exportAgentQACSV';

interface AgentQABulkActionsProps {
  itemsModel: AgentQAItemsModel;
  agentQAModel: AgentQAModel;
  jobStatus: number;
  jobName: string;
  onActionComplete: () => void;
}

const AgentQABulkActions: React.FC<AgentQABulkActionsProps> = ({
  itemsModel,
  agentQAModel,
  jobStatus,
  jobName,
  onActionComplete,
}) => {
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Calculate failed items count (sum of fail_count from all grouped items)
  const failedCount = useMemo(() => {
    if (!itemsModel.gridData || !Array.isArray(itemsModel.gridData)) {
      return 0;
    }

    const groups = itemsModel.gridData as IAgentQAGroupedItem[];
    return groups.reduce((total, group) => total + group.fail_count, 0);
  }, [itemsModel.gridData]);

  const handleBulkMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setBulkMenuAnchor(event.currentTarget);
  };

  const handleBulkMenuClose = () => {
    setBulkMenuAnchor(null);
  };

  const handleRetryFailed = async () => {
    handleBulkMenuClose();
    if (isProcessing) return;

    setIsProcessing(true);
    try {
      const result = await agentQAModel.retryFailedItems(agentQAModel.itemId);
      if (result && result.data && result.data.id) {
        // Refresh the model to update the list
        await agentQAModel.reload();
        onActionComplete();
      }
    } catch (error) {
      console.error('Error during retry failed items:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadCSV = () => {
    handleBulkMenuClose();
    const groups = (itemsModel.gridData as IAgentQAGroupedItem[]) || [];
    exportAgentQACSV(groups, jobName);
  };

  // Only render if job is done
  if (jobStatus !== AgentQAJobStatus.DONE) {
    return null;
  }

  return (
    <>
      <BulkActionsButton onClick={handleBulkMenuClick} disabled={isProcessing || itemsModel.loadingGrid}>
        {isProcessing ? 'Processing...' : 'Actions'}
      </BulkActionsButton>
      <Menu anchorEl={bulkMenuAnchor} open={Boolean(bulkMenuAnchor)} onClose={handleBulkMenuClose}>
        <MenuItem onClick={handleDownloadCSV}>Download CSV</MenuItem>
        {failedCount > 0 && (
          <MenuItem onClick={handleRetryFailed}>Retry Failed Items ({failedCount})</MenuItem>
        )}
      </Menu>
    </>
  );
};

export default AgentQABulkActions;

const BulkActionsButton = styled.button`
  padding: 8px 16px;
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.3s;

  &:hover:not(:disabled) {
    background-color: #45a049;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;
